/**
 * Hook for persisting Creative Library view state (filters, sort, view mode) per-project.
 * Stores preferences in localStorage, keyed by projectId.
 * Search is transient and not persisted.
 */

import * as React from 'react';

import type { FilterState, ViewMode } from '@/components/creative/FilterBar';

const STORAGE_KEY_PREFIX = 'adverdict_creative_view_';

interface CreativeViewState {
  filters: FilterState;
  viewMode: ViewMode;
}

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  decision: 'all',
  mediaType: 'all',
  objective: 'all',
  placement: 'all',
  taxonomyFilters: [],
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const DEFAULT_STATE: CreativeViewState = {
  filters: DEFAULT_FILTERS,
  viewMode: 'grid',
};

export function useCreativeViewState(projectId: string) {
  const storageKey = `${STORAGE_KEY_PREFIX}${projectId}`;

  const [state, setStateInternal] = React.useState<CreativeViewState>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Restore persisted values, but search is always empty on load
        return {
          viewMode: parsed.viewMode ?? DEFAULT_STATE.viewMode,
          filters: { ...DEFAULT_STATE.filters, ...parsed.filters, search: '' },
        };
      }
    } catch {
      /* ignore parse errors */
    }
    return DEFAULT_STATE;
  });

  const setState = React.useCallback(
    (update: Partial<CreativeViewState>) => {
      setStateInternal((prev) => {
        const next = {
          viewMode: update.viewMode ?? prev.viewMode,
          filters: update.filters ? { ...prev.filters, ...update.filters } : prev.filters,
        };
        // Persist everything except search
        try {
          const toPersist = {
            viewMode: next.viewMode,
            filters: { ...next.filters, search: undefined },
          };
          localStorage.setItem(storageKey, JSON.stringify(toPersist));
        } catch {
          /* ignore quota errors */
        }
        return next;
      });
    },
    [storageKey]
  );

  return [state, setState] as const;
}
